/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Toster from "../../../common/Toster";
import TimePicker from "../../../common/TimePicker";
import {
    getActivityById,
    getActivityTickets,
    getActivityTicketTypePrices,
    createActivityPriceForTicketType,
    updateActivityTicketTypePrice,
    getActivitySeasonalPrices,
    createActivitySeasonalPrice,
    deleteActivitySeasonalPrice,
    getActivityCloseOutDays,
    createActivityCloseOutDays,
    deleteActivityCloseOutDays,
    getActivityTimeSlotPricing,
    createActivityTimeSlot,
    deleteActivityTimeSlot,
    syncActivityTimeSlotTicketPrices,
} from "../../../../services/activityService";
import {
    INP,
    PricingModeSwitch,
    CollapsibleBlock,
    SlotPriceTable,
    TicketPriceTable,
    SeasonTicketTable,
    SeasonSlotPriceTable,
} from "../pricing/pricingCompactUi";
import {
    isValidAdminCharge,
    DEFAULT_ADMIN_CHARGE,
} from "../../../../constants/adminCharge";

const WEEKDAYS = [
    { day: "Sun", full: "Sunday" },
    { day: "Mon", full: "Monday" },
    { day: "Tue", full: "Tuesday" },
    { day: "Wed", full: "Wednesday" },
    { day: "Thu", full: "Thursday" },
    { day: "Fri", full: "Friday" },
    { day: "Sat", full: "Saturday" },
];

function formatTime(t) {
    const s = (t ?? "").toString();
    const m = s.match(/^(\d{1,2}):(\d{2})/);
    if (!m) return s;
    let hh = Number(m[1]);
    const mm = m[2];
    const ampm = hh >= 12 ? "PM" : "AM";
    hh = hh % 12;
    if (hh === 0) hh = 12;
    return `${hh}:${mm} ${ampm}`;
}

const emptySlotErrors = () => ({ start_time: "", end_time: "" });

const parseSlotFieldErrors = (error) => {
    const apiErrors = error?.response?.data?.errors;
    if (!apiErrors) return null;
    return {
        start_time: apiErrors.start_time?.[0] || "",
        end_time: apiErrors.end_time?.[0] || "",
    };
};

const validateSlotTimes = (start, end) => {
    if (!start || !end) return emptySlotErrors();
    if (end <= start) {
        return { start_time: "", end_time: "End time must be after start time." };
    }
    return emptySlotErrors();
};

const slotKey = (id) => Number(id);
const ticketKey = (id) => Number(id);

const hasSlotPriceValue = (value) =>
    value !== undefined && value !== null && String(value).trim() !== "";

const cloneSlotTicketPrices = (state) => {
    const cloned = {};
    Object.entries(state || {}).forEach(([slotId, tickets]) => {
        const sid = slotKey(slotId);
        if (!Number.isFinite(sid) || sid <= 0) return;
        cloned[sid] = {};
        Object.entries(tickets || {}).forEach(([ticketId, row]) => {
            const tid = ticketKey(ticketId);
            if (!Number.isFinite(tid) || tid <= 0) return;
            cloned[sid][tid] = { ...(row || {}) };
        });
    });
    return cloned;
};

const getSlotTicketRow = (state, slotId, ticketId) => {
    const sid = slotKey(slotId);
    const tid = ticketKey(ticketId);
    return state?.[sid]?.[tid] || {};
};

const ActivityPricing = ({ activityId, registerSave, onValidityChange, hideCloseOutSection = false, hideSlotManagement = false }) => {
    const isBuilderMode = Boolean(registerSave);
    const [formData, setFormData] = useState([]);
    const [expandedTickets, setExpandedTickets] = useState(null);
    const [expandedSeasons, setExpandedSeasons] = useState({});
    const [expandedSeasonTimeSlots, setExpandedSeasonTimeSlots] = useState({});
    const [expandedTimeSlots, setExpandedTimeSlots] = useState({});
    const [pricingMode, setPricingMode] = useState("pax"); // 'pax' or 'full'

    // Time-slot-based pricing state
    const [newSlotStart, setNewSlotStart] = useState("");
    const [newSlotEnd, setNewSlotEnd] = useState("");
    const [slotFieldErrors, setSlotFieldErrors] = useState(emptySlotErrors);
    const [slotTicketPrices, setSlotTicketPrices] = useState({}); // { [slotId]: { [ticketTypeId]: { price, childPrice } } }
    
    // UI State
    const [showToster, setShowToster] = useState(false);
    const [toasterMessage, setToasterMessage] = useState("");
    const [toasterType, setToasterType] = useState("success");
    const [isSaving, setIsSaving] = useState(false);

    // Guide State
    const [isGuideEnabled, setIsGuideEnabled] = useState(false);
    const [guidePrice, setGuidePrice] = useState("");

    // Seasonal State
    const [isSeasonEnabled, setIsSeasonEnabled] = useState(false);
    const [seasons, setSeasons] = useState([]);

    // Close Out State
    const [isCloseOutEnabled, setIsCloseOutEnabled] = useState(false);
    const [closeOutPeriods, setCloseOutPeriods] = useState([]);
    const [selectedDays, setSelectedDays] = useState([]);

    const queryClient = useQueryClient();

    // Queries
    const { data: activityResponse } = useQuery({
        queryKey: ["activity", activityId],
        queryFn: () => getActivityById(activityId),
        enabled: !!activityId,
    });

    const isTimeSlotBased = Number(activityResponse?.data?.time_slot_based) === 1;

    const { data: ticketsResponse, isLoading: isLoadingTickets } = useQuery({
        queryKey: ["activityTickets", activityId],
        queryFn: () => getActivityTickets(activityId),
        enabled: !!activityId,
    });

    const { data: pricesResponse, isLoading: isLoadingPrices } = useQuery({
        queryKey: ["activityTicketTypePrices", activityId],
        queryFn: () => getActivityTicketTypePrices(activityId),
        enabled: !!activityId,
    });

    const { data: seasonalResponse } = useQuery({
        queryKey: ["getActivitySeasonal", activityId],
        queryFn: () => getActivitySeasonalPrices(activityId),
        enabled: !!activityId,
    });

    const { data: closeOutResponse } = useQuery({
        queryKey: ["getActivityCloseOut", activityId],
        queryFn: () => getActivityCloseOutDays(activityId),
        enabled: !!activityId,
    });

    const { data: timeSlotPricingResponse, isLoading: isLoadingTimeSlotPricing } = useQuery({
        queryKey: ["getActivityTimeSlotPricing", activityId],
        queryFn: () => getActivityTimeSlotPricing(activityId),
        enabled: !!activityId && isTimeSlotBased,
    });

    // Initialize Form Data
    useEffect(() => {
        if (!ticketsResponse?.data || isLoadingTickets) return;

        const tickets = ticketsResponse.data;
        const prices = pricesResponse?.data || [];

        if (!isLoadingPrices && tickets.length === 0) {
            setFormData([]);
            return;
        }

        if (isLoadingPrices) return;

        // Determine correct mode first
        const currentMode = prices.length > 0 && prices[0].rate_type ? prices[0].rate_type : pricingMode;
        
        if (prices.length > 0 && prices[0].rate_type && prices[0].rate_type !== pricingMode) {
            setPricingMode(prices[0].rate_type);
        }

        const transform = tickets.map(ticket => {
            const existing = prices.find(p => p.activity_ticket_type_id === ticket.id);
            return {
                id: ticket.id,
                type: ticket.name,
                priceId: existing?.id || null,
                price: currentMode === 'full' ? (existing?.full_rate || 0) : (existing?.adult_price || 0),
                childPrice: existing?.child_price || "",
                discount: existing?.discount || 0,
                adminCharge: existing?.admin_charge || DEFAULT_ADMIN_CHARGE,
                maximumAllowedBookingsPerUser: existing?.maximum_allowed_bookings_per_user || 1,
                isActive: existing?.is_active ?? 1,
                description: existing?.description || "",
                child_description: existing?.child_description || "",
                hasExistingData: !!existing,
                slotPrices: {},
            };
        });

        if (prices.length > 0) {
            if (prices[0].guide_rate > 0) {
                setIsGuideEnabled(true);
                setGuidePrice(prices[0].guide_rate);
            }
        }

        setFormData(transform);
        if (transform.length > 0 && !expandedTickets) {
            setExpandedTickets(transform[0].id);
        }
    }, [ticketsResponse, pricesResponse, isLoadingTickets, isLoadingPrices]);

    // Initialize slot-ticket prices when time-slot-based (merge API data; keep in-progress edits)
    useEffect(() => {
        if (!isTimeSlotBased) return;
        const slots = timeSlotPricingResponse?.data;
        if (!Array.isArray(slots)) return;
        setSlotTicketPrices((prev) => {
            const next = cloneSlotTicketPrices(prev);
            slots.forEach((s) => {
                const sid = slotKey(s?.id);
                if (!Number.isFinite(sid) || sid <= 0) return;
                if (!next[sid]) next[sid] = {};
                const tp = Array.isArray(s?.ticket_prices)
                    ? s.ticket_prices
                    : Array.isArray(s?.ticketPrices)
                    ? s.ticketPrices
                    : [];
                tp.forEach((row) => {
                    const tid = ticketKey(row?.activity_ticket_type_id);
                    if (!Number.isFinite(tid) || tid <= 0) return;
                    const existing = next[sid][tid];
                    if (
                        existing &&
                        (hasSlotPriceValue(existing.price) ||
                            (pricingMode === "pax" && hasSlotPriceValue(existing.childPrice)))
                    ) {
                        return;
                    }
                    next[sid][tid] = {
                        price:
                            row?.rate_type === "full"
                                ? row?.full_rate ?? ""
                                : row?.adult_price ?? "",
                        childPrice: row?.child_price ?? "",
                        rate_type: row?.rate_type || pricingMode,
                    };
                });
            });
            return next;
        });
    }, [isTimeSlotBased, timeSlotPricingResponse, pricingMode]);

    // Initialize Seasonal
    useEffect(() => {
        if (seasonalResponse?.data?.length > 0) {
            setIsSeasonEnabled(true);
            const grouped = seasonalResponse.data.reduce((acc, curr) => {
                const key = `${curr.start_date}_${curr.end_date}`;
                if (!acc[key]) {
                    acc[key] = {
                        id: Date.now() + Math.random(),
                        name: curr.name || "",
                        startDate: curr.start_date,
                        endDate: curr.end_date,
                        tickets: formData.map(f => ({ id: f.id, type: f.type, price: 0, childPrice: "", slotPrices: {} }))
                    };
                }
                const ticketIndex = acc[key].tickets.findIndex(t => t.id === curr.activity_ticket_type_id);
                if (ticketIndex > -1) {
                    const t = acc[key].tickets[ticketIndex];
                    t.price = curr.rate_type === 'full' ? curr.full_rate : curr.adult_price;
                    t.childPrice = curr.child_price;
                    t.seasonalPriceId = curr.id;
                    t.slotPrices = {};
                    const tsList = curr.time_slots || curr.timeSlots || [];
                    if (isTimeSlotBased && Array.isArray(tsList)) {
                        tsList.forEach((ts) => {
                            const sid = ts.activity_time_slot_id ?? ts.activityTimeSlotId;
                            if (sid == null) return;
                            const rt = curr.rate_type;
                            const rowPrice = rt === "full" ? ts.full_rate ?? "" : ts.adult_price ?? "";
                            t.slotPrices[sid] = {
                                price: rowPrice === null || rowPrice === undefined ? "" : rowPrice,
                                childPrice: ts.child_price ?? "",
                            };
                        });
                    }
                }
                return acc;
            }, {});
            setSeasons(Object.values(grouped));
        }
    }, [seasonalResponse, formData.length, isTimeSlotBased]);


    // Initialize Close Out
    useEffect(() => {
        if (closeOutResponse?.data?.length > 0) {
            setIsCloseOutEnabled(true);
            setCloseOutPeriods(closeOutResponse.data.map(co => ({
                id: co.id,
                startDate: co.start_date,
                endDate: co.end_date
            })));
            const first = closeOutResponse.data[0];
            const applicable = first.applicable_days || first.applicableDays || null;
            if (applicable) {
                const active = [];
                Object.entries(applicable).forEach(([day, val]) => {
                    if (val && ["sun", "mon", "tue", "wed", "thu", "fri", "sat"].includes(day.toLowerCase())) {
                        active.push(day.charAt(0).toUpperCase() + day.slice(1).toLowerCase());
                    }
                });
                setSelectedDays(active);
            }
        }
    }, [closeOutResponse]);

    // Helper functions
    const getTodayDate = () => new Date().toISOString().split("T")[0];

    const handleTicketTypeChange = (id, field, value) => {
        setFormData(prev => prev.map(t => t.id === id ? { ...t, [field]: value } : t));
    };

    const toggleTicket = (id) => setExpandedTickets(expandedTickets === id ? null : id);

    const isSeasonExpanded = (seasonId) => expandedSeasons[seasonId] === true;

    const toggleSeasonExpanded = (seasonId) => {
        setExpandedSeasons((prev) => ({ ...prev, [seasonId]: !prev[seasonId] }));
    };

    const seasonSlotKey = (seasonId, slotId) => `${seasonId}-${slotId}`;

    const isSeasonSlotExpanded = (seasonId, slotId) =>
        expandedSeasonTimeSlots[seasonSlotKey(seasonId, slotId)] === true;

    const toggleSeasonSlotExpanded = (seasonId, slotId) => {
        const key = seasonSlotKey(seasonId, slotId);
        setExpandedSeasonTimeSlots((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const isPricingSlotExpanded = (slotId) => expandedTimeSlots[slotId] === true;

    const togglePricingSlotExpanded = (slotId) => {
        setExpandedTimeSlots((prev) => ({ ...prev, [slotId]: !prev[slotId] }));
    };

    const slotRow = (slotId, ticketId) => getSlotTicketRow(slotTicketPrices, slotId, ticketId);

    const getSeasonSlotPrice = (ticket, slotId) => {
        const sid = Number(slotId);
        return ticket.slotPrices?.[sid] ?? ticket.slotPrices?.[String(sid)] ?? {};
    };

    const handlePricingModeToggle = (mode) => setPricingMode(mode);
    const handleGuideToggle = (enabled) => {
        setIsGuideEnabled(enabled);
        if (!enabled) setGuidePrice("");
    };

    const handleSeasonToggle = (enabled) => {
        setIsSeasonEnabled(enabled);
        if (enabled && seasons.length === 0) addSeason();
    };

    const addSeason = () => {
        const newId = Date.now();
        setSeasons((prev) => [
            ...prev,
            {
                id: newId,
                name: "",
                startDate: "",
                endDate: "",
                tickets: formData.map((t) => ({
                    id: t.id,
                    type: t.type,
                    price: 0,
                    childPrice: "",
                    slotPrices: {},
                })),
            },
        ]);
        setExpandedSeasons((prev) => ({ ...prev, [newId]: true }));
    };

    const removeSeason = (id) => {
        setSeasons((prev) => prev.filter((s) => s.id !== id));
        setExpandedSeasons((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
        setExpandedSeasonTimeSlots((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((key) => {
                if (key.startsWith(`${id}-`)) delete next[key];
            });
            return next;
        });
    };
    const updateSeasonDates = (id, field, value) => setSeasons(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
    const updateSeasonTicket = (seasonId, ticketId, field, value) => {
        setSeasons(prev => prev.map(s => s.id === seasonId ? {
            ...s,
            tickets: s.tickets.map(t => t.id === ticketId ? { ...t, [field]: value } : t)
        } : s));
    };

    const updateSeasonTicketSlotPrice = (seasonId, ticketId, slotId, field, value) => {
        const sid = Number(slotId);
        setSeasons(prev => prev.map((s) => {
            if (s.id !== seasonId) return s;
            return {
                ...s,
                tickets: s.tickets.map((t) => {
                    if (t.id !== ticketId) return t;
                    const nextSlotPrices = { ...(t.slotPrices || {}) };
                    const key = Number.isFinite(sid) && sid > 0 ? sid : slotId;
                    nextSlotPrices[key] = { ...(nextSlotPrices[key] || {}), [field]: value };
                    return { ...t, slotPrices: nextSlotPrices };
                }),
            };
        }));
    };

    const handleCloseOutToggle = (enabled) => {
        setIsCloseOutEnabled(enabled);
        if (enabled && closeOutPeriods.length === 0) addCloseOutPeriod();
    };

    const addCloseOutPeriod = () => setCloseOutPeriods(prev => [...prev, { id: Date.now(), startDate: "", endDate: "" }]);
    const removeCloseOutPeriod = (id) => setCloseOutPeriods(prev => prev.filter(p => p.id !== id));
    const updateCloseOutDates = (id, field, value) => setCloseOutPeriods(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));
    const handleDayToggle = (day) => setSelectedDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
    const handleAllDaysToggle = () => setSelectedDays(selectedDays.length === WEEKDAYS.length ? [] : WEEKDAYS.map(d => d.day));

    // Validation
    const validateAllFields = () => {
        const errors = [];

        if (isTimeSlotBased) {
            const slots = Array.isArray(timeSlotPricingResponse?.data) ? timeSlotPricingResponse.data : [];
            if (slots.length === 0) errors.push("Add at least one time slot.");
            const tickets = Array.isArray(ticketsResponse?.data) ? ticketsResponse.data : [];
            slots.forEach((s) => {
                const sid = Number(s?.id);
                const perSlot = slotTicketPrices?.[sid] || {};
                tickets.forEach((t) => {
                    const tid = Number(t?.id);
                    const row = perSlot?.[tid] || {};
                    const v = row?.price;
                    if (v === "" || v === null || v === undefined || Number(v) <= 0) {
                        errors.push(`Slot ${formatTime(s.start_time)}-${formatTime(s.end_time)}: ${t.name}: Price is required and must be > 0`);
                    }
                });
            });

            // Keep existing ticket-level validations (discount/admin charge/description) in slot mode too
            formData.forEach((t) => {
                const discountNum = t.discount === "" || t.discount === null || t.discount === undefined ? 0 : Number(t.discount);
                if (Number.isFinite(discountNum) && discountNum > 100) errors.push(`${t.type}: Discount must be 100 or less`);
                if (!isValidAdminCharge(t.adminCharge)) {
                    errors.push(`${t.type}: Admin charge must be 5%, 10%, 15%, or 20%`);
                }
            });
        } else {
            formData.forEach(t => {
                if (!t.price || t.price <= 0) errors.push(`${t.type}: Price is required and must be > 0`);
                const discountNum = t.discount === "" || t.discount === null || t.discount === undefined ? 0 : Number(t.discount);
                if (Number.isFinite(discountNum) && discountNum > 100) errors.push(`${t.type}: Discount must be 100 or less`);
            });
        }

        if (isGuideEnabled && (!guidePrice || guidePrice <= 0)) errors.push("Guide: Price is required and must be > 0");
        if (isSeasonEnabled) {
            seasons.forEach((s, i) => {
                const label = s.name?.trim() || `Season ${i + 1}`;
                if (!s.name?.trim()) errors.push(`${label}: Season name is required`);
                if (!s.startDate || !s.endDate) errors.push(`${label}: Start and end dates are required`);
                const hasWin = s.tickets.some(t => Number(t.price) > 0);
                const hasSlotSeason = isTimeSlotBased && s.tickets.some(t =>
                    Object.values(t.slotPrices || {}).some(row => Number(row?.price) > 0)
                );
                if (!hasWin && !hasSlotSeason) errors.push(`${label}: Set a default seasonal price or at least one per-slot seasonal price`);
            });
        }
        if (isCloseOutEnabled && !hideCloseOutSection) {
            if (selectedDays.length === 0) {
                errors.push("Close Out: At least one weekday must be selected");
            }
            const incompletePeriod = closeOutPeriods.some(
                (p) => Boolean(p.startDate) !== Boolean(p.endDate)
            );
            if (incompletePeriod) {
                errors.push(
                    "Close Out: Each period needs both start and end dates, or leave both empty"
                );
            }
        }
        return errors;
    };

    useEffect(() => {
        const errors = validateAllFields();
        onValidityChange?.(errors.length === 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pricingMode, formData, isGuideEnabled, guidePrice, isSeasonEnabled, seasons, isCloseOutEnabled, closeOutPeriods, selectedDays, slotTicketPrices, isTimeSlotBased, timeSlotPricingResponse]);

    const handleSavePricing = async () => {
        const errors = validateAllFields();
        if (errors.length > 0) {
            const message = errors.join(" | ");
            if (!isBuilderMode) {
                setToasterMessage(message);
                setToasterType("error");
                setShowToster(true);
            }
            throw new Error(message);
        }

        setIsSaving(true);
        try {
            // 1. Base Prices (only when not time-slot-based)
            if (!isTimeSlotBased) {
                await Promise.all(formData.map(t => {
                    const payload = {
                        rate_type: pricingMode,
                        full_rate: pricingMode === 'full' ? t.price : 0,
                        adult_price: pricingMode === 'pax' ? t.price : 0,
                        child_price: t.childPrice || 0,
                        guide_rate: isGuideEnabled ? guidePrice : 0,
                        admin_charge: t.adminCharge,
                        maximum_allowed_bookings_per_user: t.maximumAllowedBookingsPerUser,
                        discount: t.discount,
                        status: 1
                    };
                    if (t.priceId) return updateActivityTicketTypePrice(activityId, t.priceId, payload);
                    return createActivityPriceForTicketType(activityId, t.id, payload);
                }));
            }

            // 1b. Slot Ticket Prices (only when time-slot-based)
            if (isTimeSlotBased) {
                // Save ticket-level settings too (discount/admin charge/max bookings/is active/description/etc).
                // We persist them using the existing ticket-type-prices endpoint, but the actual pricing is slot-based.
                const slotsForDefaults = Array.isArray(timeSlotPricingResponse?.data) ? timeSlotPricingResponse.data : [];
                const firstSlotId = slotsForDefaults?.[0]?.id;
                const slots = Array.isArray(timeSlotPricingResponse?.data) ? timeSlotPricingResponse.data : [];
                const tickets = Array.isArray(ticketsResponse?.data) ? ticketsResponse.data : [];

                await Promise.all(formData.map((t) => {
                    const baseFromFirstSlot = firstSlotId
                        ? getSlotTicketRow(slotTicketPrices, firstSlotId, t.id)
                        : null;
                    const basePrice = baseFromFirstSlot?.price ?? 0;
                    const baseChild = baseFromFirstSlot?.childPrice ?? 0;
                    const payload = {
                        rate_type: pricingMode,
                        full_rate: pricingMode === 'full' ? Number(basePrice || 0) : 0,
                        adult_price: pricingMode === 'pax' ? Number(basePrice || 0) : 0,
                        child_price: pricingMode === 'pax' ? Number(baseChild || 0) : 0,
                        guide_rate: isGuideEnabled ? guidePrice : 0,
                        admin_charge: t.adminCharge,
                        maximum_allowed_bookings_per_user: t.maximumAllowedBookingsPerUser,
                        discount: t.discount,
                        status: 1
                    };
                    if (t.priceId) return updateActivityTicketTypePrice(activityId, t.priceId, payload);
                    return createActivityPriceForTicketType(activityId, t.id, payload);
                }));

                await Promise.all(slots.map((s) => {
                    const sid = Number(s?.id);
                    const perSlot = slotTicketPrices?.[sid] || {};
                    const payload = {
                        rate_type: pricingMode,
                        ticket_prices: tickets.map((t) => {
                            const tid = Number(t?.id);
                            const row = perSlot?.[tid] || {};
                            return {
                                activity_ticket_type_id: tid,
                                full_rate: pricingMode === "full" ? Number(row.price || 0) : null,
                                adult_price: pricingMode === "pax" ? Number(row.price || 0) : null,
                                child_price: pricingMode === "pax" ? Number(row.childPrice || 0) : null,
                                is_active: 1,
                            };
                        }),
                    };
                    return syncActivityTimeSlotTicketPrices(activityId, sid, payload);
                }));
                queryClient.invalidateQueries(["getActivityTimeSlotPricing", activityId]);
            }

            // 2. Seasonal
            // Clean up old seasonal records first to avoid overlap validation errors
            if (seasonalResponse?.data?.length > 0) {
                await Promise.all(seasonalResponse.data.map(curr => 
                    deleteActivitySeasonalPrice(activityId, curr.id)
                ));
            }

            if (isSeasonEnabled) {
                const slotsApi = Array.isArray(timeSlotPricingResponse?.data) ? timeSlotPricingResponse.data : [];
                await Promise.all(seasons.map((s) =>
                    Promise.all(
                        s.tickets
                            .filter((st) => {
                                const win = Number(st.price) > 0;
                                if (!isTimeSlotBased) return win;
                                const anySlot = Object.values(st.slotPrices || {}).some((row) => Number(row?.price) > 0);
                                return win || anySlot;
                            })
                            .map((st) => {
                                const timeSlotsPayload =
                                    isTimeSlotBased && slotsApi.length > 0
                                        ? slotsApi
                                            .map((slot) => {
                                                const sid = Number(slot?.id);
                                                if (!Number.isFinite(sid) || sid <= 0) return null;
                                                const row = st.slotPrices?.[sid] ?? st.slotPrices?.[String(sid)];
                                                if (!row || !Number(row.price)) return null;
                                                return {
                                                    activity_time_slot_id: sid,
                                                    start_time: slot.start_time,
                                                    adult_price: pricingMode === "pax" ? Number(row.price) : null,
                                                    child_price: pricingMode === "pax" ? Number(row.childPrice || 0) : null,
                                                    full_rate: pricingMode === "full" ? Number(row.price) : null,
                                                };
                                            })
                                            .filter(Boolean)
                                        : undefined;

                                const payload = {
                                    activity_ticket_type_id: st.id,
                                    name: s.name?.trim() || "",
                                    start_date: s.startDate,
                                    end_date: s.endDate,
                                    adult_price: pricingMode === "pax" ? Number(st.price || 0) : 0,
                                    child_price: Number(st.childPrice || 0),
                                    full_rate: pricingMode === "full" ? Number(st.price || 0) : 0,
                                    rate_type: pricingMode,
                                };
                                if (timeSlotsPayload && timeSlotsPayload.length > 0) {
                                    payload.time_slots = timeSlotsPayload;
                                }
                                return createActivitySeasonalPrice(activityId, payload);
                            })
                    )
                ));
            }

            if (!hideCloseOutSection) {
            // 3. Close Outs
            // Clean up old close out records first
            if (closeOutResponse?.data?.length > 0) {
                await Promise.all(closeOutResponse.data.map(curr => 
                    deleteActivityCloseOutDays(activityId, curr.id)
                ));
            }

            if (isCloseOutEnabled) {
                const days = {
                    sun: selectedDays.includes("Sun"),
                    mon: selectedDays.includes("Mon"),
                    tue: selectedDays.includes("Tue"),
                    wed: selectedDays.includes("Wed"),
                    thu: selectedDays.includes("Thu"),
                    fri: selectedDays.includes("Fri"),
                    sat: selectedDays.includes("Sat"),
                };
                const periodStarts = closeOutPeriods
                    .map((p) => p.startDate)
                    .filter(Boolean)
                    .sort();
                const startDate = periodStarts[0] || getTodayDate();

                // Recurring weekday close-out until changed (not limited to short period ranges).
                await createActivityCloseOutDays(activityId, {
                    start_date: startDate,
                    end_date: "2099-12-31",
                    applicable_days: days,
                });
            }
            }

            if (!isBuilderMode) {
                setToasterMessage("Pricing system updated successfully!");
                setToasterType("success");
                setShowToster(true);
            }
            queryClient.invalidateQueries(["activityTicketTypePrices", activityId]);
            queryClient.invalidateQueries(["getActivityPrices", activityId]);
        } catch (err) {
            if (!isBuilderMode) {
                setToasterMessage(err.message || "Failed to update pricing");
                setToasterType("error");
                setShowToster(true);
            }
            throw err;
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        registerSave?.(() => handleSavePricing());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [registerSave, activityId, pricingMode, formData, isGuideEnabled, guidePrice, isSeasonEnabled, seasons, isCloseOutEnabled, closeOutPeriods, selectedDays, isTimeSlotBased, timeSlotPricingResponse]);

    if (isLoadingTickets || isLoadingPrices || (isTimeSlotBased && isLoadingTimeSlotPricing)) {
        return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
    }

    const timeSlots = Array.isArray(timeSlotPricingResponse?.data) ? timeSlotPricingResponse.data : [];

    const handleAddActivitySlot = async () => {
        if (!newSlotStart || !newSlotEnd) return;

        const clientErrors = validateSlotTimes(newSlotStart, newSlotEnd);
        if (clientErrors.end_time) {
            setSlotFieldErrors(clientErrors);
            return;
        }

        setSlotFieldErrors(emptySlotErrors());
        try {
            await createActivityTimeSlot(activityId, {
                start_time: newSlotStart,
                end_time: newSlotEnd,
                status: 1,
            });
            setNewSlotStart("");
            setNewSlotEnd("");
            setSlotFieldErrors(emptySlotErrors());
            queryClient.invalidateQueries(["getActivityTimeSlotPricing", activityId]);
        } catch (error) {
            const fieldErrors = parseSlotFieldErrors(error);
            if (fieldErrors) {
                setSlotFieldErrors(fieldErrors);
                return;
            }
            setToasterMessage(error?.response?.data?.message || "Failed to add time slot");
            setToasterType("error");
            setShowToster(true);
        }
    };

    const handleDeleteActivitySlot = async (slotId) => {
        await deleteActivityTimeSlot(activityId, slotId);
        queryClient.invalidateQueries(["getActivityTimeSlotPricing", activityId]);
    };

    const handleCopyPricesToAllSlots = (fromSlotId) => {
        const fromSid = slotKey(fromSlotId);
        const slots = Array.isArray(timeSlotPricingResponse?.data) ? timeSlotPricingResponse.data : [];
        if (!Number.isFinite(fromSid) || fromSid <= 0 || slots.length <= 1) return;

        const tickets = Array.isArray(ticketsResponse?.data) ? ticketsResponse.data : [];
        const sourceSlot = slotTicketPrices?.[fromSid] || {};
        const hasAnyPrice = tickets.some((t) => {
            const row = sourceSlot[ticketKey(t.id)] || {};
            return (
                hasSlotPriceValue(row.price) ||
                (pricingMode === "pax" && hasSlotPriceValue(row.childPrice))
            );
        });

        if (!hasAnyPrice) {
            setToasterMessage("Add prices to this slot before copying to other slots.");
            setToasterType("error");
            setShowToster(true);
            return;
        }

        setSlotTicketPrices((prev) => {
            const next = cloneSlotTicketPrices(prev);
            const source = next[fromSid] || {};

            slots.forEach((s) => {
                const sid = slotKey(s?.id);
                if (!Number.isFinite(sid) || sid <= 0 || sid === fromSid) return;
                if (!next[sid]) next[sid] = {};
                tickets.forEach((t) => {
                    const tid = ticketKey(t?.id);
                    if (!Number.isFinite(tid) || tid <= 0) return;
                    const srcRow = source[tid] || {};
                    next[sid][tid] = {
                        ...(next[sid][tid] || {}),
                        price: srcRow.price ?? "",
                        childPrice: srcRow.childPrice ?? "",
                        rate_type: srcRow.rate_type ?? pricingMode,
                    };
                });
            });

            return next;
        });
    };

    const handleCopyPricesToAllTickets = (fromTicketId) => {
        if (formData.length <= 1) return;

        const source = formData.find((t) => t.id === fromTicketId);
        if (!source) return;

        const hasAnyPrice =
            hasSlotPriceValue(source.price) ||
            (pricingMode === "pax" && hasSlotPriceValue(source.childPrice));

        if (!hasAnyPrice) {
            setToasterMessage("Add prices to this ticket before copying to other tickets.");
            setToasterType("error");
            setShowToster(true);
            return;
        }

        setFormData((prev) =>
            prev.map((t) => {
                if (t.id === fromTicketId) return t;
                return {
                    ...t,
                    price: source.price ?? "",
                    childPrice: source.childPrice ?? "",
                    discount: source.discount ?? "",
                    adminCharge: source.adminCharge ?? "",
                    maximumAllowedBookingsPerUser: source.maximumAllowedBookingsPerUser ?? "",
                };
            })
        );
    };

    const handleClearSeasonalSlotPrices = (seasonId, slotId) => {
        const sid = Number(slotId);
        if (!Number.isFinite(sid) || sid <= 0) return;
        setSeasons((prev) =>
            prev.map((season) => {
                if (season.id !== seasonId) return season;
                return {
                    ...season,
                    tickets: season.tickets.map((t) => {
                        const nextSlotPrices = { ...(t.slotPrices || {}) };
                        delete nextSlotPrices[sid];
                        delete nextSlotPrices[String(sid)];
                        return { ...t, slotPrices: nextSlotPrices };
                    }),
                };
            })
        );
    };

    const setSlotTicketField = (slotId, ticketTypeId, field, value) => {
        const sid = slotKey(slotId);
        const tid = ticketKey(ticketTypeId);
        setSlotTicketPrices((prev) => ({
            ...(prev || {}),
            [sid]: {
                ...((prev || {})[sid] || {}),
                [tid]: {
                    ...(((prev || {})[sid] || {})[tid] || {}),
                    [field]: value,
                },
            },
        }));
    };

    const ticketList = Array.isArray(ticketsResponse?.data) ? ticketsResponse.data : [];

    return (
        <div className={`space-y-3 ${isSaving ? "pointer-events-none opacity-50" : ""}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                <div>
                    <h4 className="text-sm font-bold text-gray-900">
                        {isTimeSlotBased ? "Slot prices" : "Ticket prices"}
                    </h4>
                    <p className="text-xs text-gray-500">
                        {isTimeSlotBased
                            ? "Expand each slot and set adult / child rates per ticket."
                            : "Expand each ticket to set rates and rules."}
                    </p>
                </div>
                <PricingModeSwitch mode={pricingMode} onChange={handlePricingModeToggle} />
            </div>

            {isTimeSlotBased ? (
                <div className="space-y-2">
                    {!hideSlotManagement && (
                        <div className="rounded-lg border border-gray-200 bg-white p-3">
                            <div className="grid grid-cols-1 items-end gap-2 sm:grid-cols-3">
                                <div>
                                    <label className="mb-1 block text-[10px] font-bold uppercase text-gray-500">
                                        Start
                                    </label>
                                    <TimePicker
                                        value={newSlotStart}
                                        onChange={(value) => {
                                            setNewSlotStart(value);
                                            setSlotFieldErrors(emptySlotErrors());
                                        }}
                                        placeholder="Select start time"
                                        error={slotFieldErrors.start_time}
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-[10px] font-bold uppercase text-gray-500">
                                        End
                                    </label>
                                    <TimePicker
                                        value={newSlotEnd}
                                        onChange={(value) => {
                                            setNewSlotEnd(value);
                                            setSlotFieldErrors(emptySlotErrors());
                                        }}
                                        placeholder="Select end time"
                                        error={slotFieldErrors.end_time}
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={handleAddActivitySlot}
                                    disabled={!newSlotStart || !newSlotEnd}
                                    className="h-8 rounded-md bg-primary px-3 text-xs font-bold text-white disabled:opacity-50"
                                >
                                    + Add slot
                                </button>
                            </div>
                        </div>
                    )}

                    {timeSlots.length === 0 ? (
                        <p className="rounded-lg border border-dashed border-gray-200 py-6 text-center text-sm text-gray-500">
                            {hideSlotManagement
                                ? "Add time slots in Availability first."
                                : "No time slots yet."}
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {timeSlots.map((s) => (
                                <CollapsibleBlock
                                    key={s.id}
                                    title={`${formatTime(s.start_time)} – ${formatTime(s.end_time)}`}
                                    subtitle={`${ticketList.length} ticket type${ticketList.length === 1 ? "" : "s"}`}
                                    open={isPricingSlotExpanded(s.id)}
                                    onToggle={() => togglePricingSlotExpanded(s.id)}
                                    actions={
                                        <>
                                            {timeSlots.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleCopyPricesToAllSlots(s.id);
                                                    }}
                                                    className="text-[10px] font-semibold text-primary hover:underline"
                                                >
                                                    Copy rates
                                                </button>
                                            )}
                                            {!hideSlotManagement && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteActivitySlot(s.id);
                                                    }}
                                                    className="text-[10px] font-semibold text-red-600 hover:underline"
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </>
                                    }
                                >
                                    <SlotPriceTable
                                        slotId={s.id}
                                        tickets={ticketList}
                                        pricingMode={pricingMode}
                                        getSlotTicketRow={slotRow}
                                        setSlotTicketField={setSlotTicketField}
                                    />
                                </CollapsibleBlock>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <div className="space-y-2">
                    {formData.map((ticket) => (
                        <CollapsibleBlock
                            key={ticket.id}
                            title={ticket.type}
                            subtitle="Rates & booking rules"
                            open={expandedTickets === ticket.id}
                            onToggle={() => toggleTicket(ticket.id)}
                            actions={
                                formData.length > 1 ? (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopyPricesToAllTickets(ticket.id);
                                        }}
                                        className="text-[10px] font-semibold text-primary hover:underline"
                                    >
                                        Copy rates
                                    </button>
                                ) : null
                            }
                        >
                            <TicketPriceTable
                                ticket={ticket}
                                pricingMode={pricingMode}
                                onTicketChange={handleTicketTypeChange}
                            />
                        </CollapsibleBlock>
                    ))}
                </div>
            )}

            {/* Guide */}
            <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5">
                <div className="flex items-center justify-between gap-2">
                    <div>
                        <h4 className="text-sm font-bold text-gray-900">Guide included</h4>
                        <p className="text-xs text-gray-500">Optional guide fee added to bookings</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => handleGuideToggle(!isGuideEnabled)}
                        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                            isGuideEnabled ? "bg-primary" : "bg-gray-200"
                        }`}
                    >
                        <span
                            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                isGuideEnabled ? "translate-x-5" : "translate-x-1"
                            }`}
                        />
                    </button>
                </div>

                {isGuideEnabled && (
                    <div className="mt-3 border-t border-gray-100 pt-3">
                        <label className="mb-1.5 block text-xs font-semibold text-gray-600">
                            Guide price <span className="text-red-500">*</span>
                        </label>
                        <div className="relative max-w-xs">
                            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                                ₹
                            </span>
                            <input
                                type="number"
                                min="0"
                                placeholder="Enter guide price"
                                value={guidePrice}
                                onChange={(e) => setGuidePrice(e.target.value)}
                                className={`${INP} pl-7`}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Season pricing */}
            <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5">
                <div className="flex items-center justify-between gap-2">
                    <div>
                        <h4 className="text-sm font-bold text-gray-900">Season pricing</h4>
                        <p className="text-xs text-gray-500">Optional peak / holiday rates by date range</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => handleSeasonToggle(!isSeasonEnabled)}
                        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                            isSeasonEnabled ? "bg-primary" : "bg-gray-200"
                        }`}
                    >
                        <span
                            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                isSeasonEnabled ? "translate-x-5" : "translate-x-1"
                            }`}
                        />
                    </button>
                </div>

                {isSeasonEnabled && (
                    <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
                        {seasons.map((season, idx) => (
                            <CollapsibleBlock
                                key={season.id}
                                title={season.name?.trim() || `Season ${idx + 1}`}
                                subtitle={
                                    season.startDate || season.endDate
                                        ? `${season.startDate || "…"} → ${season.endDate || "…"}`
                                        : "Set name and dates"
                                }
                                open={isSeasonExpanded(season.id)}
                                onToggle={() => toggleSeasonExpanded(season.id)}
                                actions={
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeSeason(season.id);
                                        }}
                                        className="text-[10px] font-semibold text-red-500 hover:underline"
                                    >
                                        Remove
                                    </button>
                                }
                            >
                                <div className="space-y-3">
                                    <div>
                                        <label className="mb-1 block text-[10px] font-bold uppercase text-gray-500">
                                            Season name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={season.name || ""}
                                            onChange={(e) =>
                                                updateSeasonDates(season.id, "name", e.target.value)
                                            }
                                            placeholder="e.g. Peak season, Christmas holidays"
                                            className={INP}
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="mb-1 block text-[10px] font-bold uppercase text-gray-500">
                                                From
                                            </label>
                                            <input
                                                type="date"
                                                value={season.startDate}
                                                onChange={(e) =>
                                                    updateSeasonDates(season.id, "startDate", e.target.value)
                                                }
                                                min={getTodayDate()}
                                                className={INP}
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-[10px] font-bold uppercase text-gray-500">
                                                To
                                            </label>
                                            <input
                                                type="date"
                                                value={season.endDate}
                                                onChange={(e) =>
                                                    updateSeasonDates(season.id, "endDate", e.target.value)
                                                }
                                                min={season.startDate || getTodayDate()}
                                                className={INP}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <p className="mb-1.5 text-xs font-semibold text-gray-600">
                                            Default season rates
                                        </p>
                                        <SeasonTicketTable
                                            season={season}
                                            tickets={season.tickets}
                                            pricingMode={pricingMode}
                                            onUpdate={updateSeasonTicket}
                                        />
                                    </div>

                                    {isTimeSlotBased && timeSlots.length > 0 && (
                                        <div className="space-y-2">
                                            <p className="text-xs font-semibold text-gray-600">
                                                Per-slot overrides (optional)
                                            </p>
                                            {timeSlots.map((s) => (
                                                <CollapsibleBlock
                                                    key={s.id}
                                                    title={`${formatTime(s.start_time)} – ${formatTime(s.end_time)}`}
                                                    open={isSeasonSlotExpanded(season.id, s.id)}
                                                    onToggle={() =>
                                                        toggleSeasonSlotExpanded(season.id, s.id)
                                                    }
                                                    actions={
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleClearSeasonalSlotPrices(
                                                                    season.id,
                                                                    s.id
                                                                );
                                                            }}
                                                            className="text-[10px] font-semibold text-red-600 hover:underline"
                                                        >
                                                            Clear
                                                        </button>
                                                    }
                                                >
                                                    <SeasonSlotPriceTable
                                                        season={season}
                                                        slot={s}
                                                        tickets={season.tickets}
                                                        pricingMode={pricingMode}
                                                        getSlotPrice={getSeasonSlotPrice}
                                                        onSlotPriceChange={updateSeasonTicketSlotPrice}
                                                    />
                                                </CollapsibleBlock>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </CollapsibleBlock>
                        ))}
                        <button
                            type="button"
                            onClick={addSeason}
                            className="h-8 w-full rounded-md border border-dashed border-primary text-xs font-bold text-primary"
                        >
                            + Add season
                        </button>
                    </div>
                )}
            </div>

            {/* Close Out Section */}
            {!hideCloseOutSection && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-bold text-gray-900">Close out</h4>
                    <button
                        type="button"
                        onClick={() => handleCloseOutToggle(!isCloseOutEnabled)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${isCloseOutEnabled ? "bg-primary" : "bg-gray-200"}`}
                    >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${isCloseOutEnabled ? "translate-x-5" : "translate-x-1"}`} />
                    </button>
                </div>
                {isCloseOutEnabled && (
                    <div className="space-y-3">
                        {closeOutPeriods.map((p, idx) => (
                            <div key={p.id} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between mb-2">
                                    <h5 className="text-sm font-semibold">Period {idx + 1}</h5>
                                    <button onClick={() => removeCloseOutPeriod(p.id)} className="text-red-500 text-xs font-semibold">Remove</button>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <input type="date" value={p.startDate} onChange={(e) => updateCloseOutDates(p.id, "startDate", e.target.value)} min={getTodayDate()} className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md" />
                                    <input type="date" value={p.endDate} onChange={(e) => updateCloseOutDates(p.id, "endDate", e.target.value)} min={p.startDate || getTodayDate()} className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md" />
                                </div>
                            </div>
                        ))}
                        <button onClick={addCloseOutPeriod} className="w-full h-8 border border-dashed border-primary text-primary text-[11px] font-bold rounded-lg uppercase tracking-widest">+ Add Period</button>
                        
                        <div className="pt-4 border-t border-gray-100">
                            <div className="flex items-center justify-between mb-3">
                                <h5 className="text-sm font-semibold">Select Applicable Days</h5>
                                <button onClick={handleAllDaysToggle} className="text-xs text-primary font-bold">{selectedDays.length === WEEKDAYS.length ? "Clear All" : "Select All"}</button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {WEEKDAYS.map(w => (
                                    <button 
                                        key={w.day} 
                                        onClick={() => handleDayToggle(w.day)}
                                        className={`px-3 h-8 text-[11px] font-semibold rounded-lg transition-colors border ${selectedDays.includes(w.day) ? "bg-red-100 text-red-700 border-red-200" : "bg-gray-50 text-gray-600 border-gray-200"}`}
                                    >
                                        {w.day}
                                    </button>
                                ))}
                            </div>
                            <p className="text-[11px] text-gray-500 mt-2">
                                Selected weekdays stay closed every week. Period dates are optional — if left empty, the rule starts from today.
                            </p>
                        </div>
                    </div>
                )}
            </div>
            )}

            {/* Pricing is saved via footer "Save & Continue" */}

            {showToster && (
                <Toster
                    message={toasterMessage}
                    type={toasterType}
                    onClose={() => setShowToster(false)}
                />
            )}
        </div>
    );
};

export default ActivityPricing;
